import React, { useState, useEffect } from 'react';
import { FaFolder, FaFolderOpen, FaFileAlt, FaChevronRight, FaChevronDown, FaBook } from 'react-icons/fa';
import PropTypes from 'prop-types';
import { ListGroup } from 'react-bootstrap';
import './CuentasTreeView.css'; // Optional CSS for custom styling

const TreeNode = ({ node, onSelect, selectedNodeId, onContextMenu }) => {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = node.children && node.children.length > 0;
  
  const handleToggle = (e) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };
  
  const handleSelect = (e) => {
    e.stopPropagation();
    if (onSelect) {
      onSelect(node);
    }
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onContextMenu) {
      onContextMenu(e, node);
    }
  };
  
  const isSelected = selectedNodeId === node.idCatCuentaContable;
  
  return (
    <ListGroup.Item 
      className={`border-0 p-0 ${isSelected ? 'bg-light' : ''}`}
      style={{ backgroundColor: 'transparent' }}
    >
      <div 
        className={`d-flex align-items-center py-1 px-2 cursor-pointer rounded tree-node-hover ${isSelected ? 'text-primary fw-bold' : ''}`}
        onClick={handleSelect}
        onContextMenu={handleContextMenu}
        style={{ cursor: 'pointer' }}
      >
        {/* Expand/Collapse Icon */}
        <div 
          onClick={hasChildren ? handleToggle : undefined} 
          className="me-2 text-muted"
          style={{ width: '16px', visibility: hasChildren ? 'visible' : 'hidden' }}
        >
          {expanded ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
        </div>
        
        {/* Folder/File/Book Icon */}
        <div className="me-2 text-warning">
          {node.iTipoNivelCuenta?.toString() === '1' ? (
            <FaBook className="text-info" />
          ) : !hasChildren ? (
            <FaFileAlt className="text-secondary" />
          ) : expanded ? (
            <FaFolderOpen />
          ) : (
            <FaFolder />
          )}
        </div>
        
        {/* Node Label */}
        <span className="text-truncate">
          <span className="me-2 text-muted small">{node.vCodigoCuenta}</span>
          {node.vNombreCuenta}
        </span>
      </div>
      
      {/* Children rendering */}
      {hasChildren && expanded && (
        <div className="ms-4 border-start ps-2 my-1">
          <ListGroup variant="flush">
            {node.children.map((child) => (
              <TreeNode 
                key={child.idCatCuentaContable} 
                node={child} 
                onSelect={onSelect} 
                selectedNodeId={selectedNodeId} 
                onContextMenu={onContextMenu}
              />
            ))}
          </ListGroup>
        </div>
      )}
    </ListGroup.Item>
  );
};

const CuentasTreeView = ({ data, onSelectAccount, selectedAccountId, onAddChild, onDelete }) => {
  const [contextMenu, setContextMenu] = useState(null);

  const handleContextMenu = (e, node) => {
    e.preventDefault();
    setContextMenu({
      mouseX: e.clientX - 2,
      mouseY: e.clientY - 4,
      node: node
    });
  };

  const handleClose = () => {
    setContextMenu(null);
  };

  const handleAddChild = () => {
    if (onAddChild && contextMenu?.node) {
      onAddChild(contextMenu.node);
    }
    handleClose();
  };

  const handleDelete = () => {
    if (contextMenu?.node && onDelete) {
      onDelete(contextMenu.node);
    }
    handleClose();
  };

  useEffect(() => {
    const handleClick = () => handleClose();
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  if (!data || data.length === 0) {
    return <div className="text-muted p-3 text-center">No hay cuentas disponibles</div>;
  }
  
  return (
    <div className="cuentas-tree-view border rounded p-2" style={{ maxHeight: '600px', overflowY: 'auto', backgroundColor: '#fff', position: 'relative' }}>
      <ListGroup variant="flush">
        {data.map((node) => (
          <TreeNode 
            key={node.idCatCuentaContable} 
            node={node} 
            onSelect={onSelectAccount} 
            selectedNodeId={selectedAccountId} 
            onContextMenu={handleContextMenu}
          />
        ))}
      </ListGroup>

      {contextMenu && (
        <div 
          style={{
            position: 'fixed',
            top: contextMenu.mouseY,
            left: contextMenu.mouseX,
            zIndex: 9999,
            backgroundColor: 'white',
            boxShadow: '0px 2px 10px rgba(0,0,0,0.2)',
            borderRadius: '4px',
            padding: '4px 0',
            minWidth: '150px'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div 
            className="dropdown-item text-primary" 
            onClick={handleAddChild}
            style={{ cursor: 'pointer', padding: '0.5rem 1rem' }}
          >
            Agregar cuenta hija
          </div>
          <div 
            className="dropdown-item text-danger" 
            onClick={handleDelete}
            style={{ cursor: 'pointer', padding: '0.5rem 1rem' }}
          >
            Eliminar cuenta
          </div>
        </div>
      )}

    </div>
  );
};

TreeNode.propTypes = {
  node: PropTypes.shape({
    idCatCuentaContable: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    iTipoNivelCuenta: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    vCodigoCuenta: PropTypes.string,
    vNombreCuenta: PropTypes.string,
    children: PropTypes.array
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
  selectedNodeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onContextMenu: PropTypes.func.isRequired
};

CuentasTreeView.propTypes = {
  data: PropTypes.array.isRequired,
  onSelectAccount: PropTypes.func.isRequired,
  selectedAccountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onAddChild: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
};

export default CuentasTreeView;
